import { filterNavByPermission, usePermission } from '@realtyfeed/auth';
import { useLayoutStore } from '@realtyfeed/store';
import { SidebarRowContent, sidebarRowClass } from '@realtyfeed/ui';
import { cn } from '@realtyfeed/utils';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { isNavGroup, type NavGroupItem, type NavItem, type NavLinkItem } from '../type';

/**
 * A nav entry's label, through i18next.
 *
 * Keyed by the entry's `id` under `nav.items` (`locales/<lang>/common.json`). The config's own
 * `title` is the fallback, so an entry added without a bundle key still renders in English
 * rather than as a raw key — and `navigation.test.ts` fails until the key is added.
 */
const useNavTitle = () => {
  const { t } = useTranslation();
  return (item: NavItem): string => t(`nav.items.${item.id}`, { defaultValue: item.title });
};

/**
 * Whether a path prefix is the one currently rendered.
 *
 * Prefix-matched, not exact: `/services-and-configs/data-feed-filters/edit/12`
 * must keep the "Data Feed Filters" row lit. The trailing-slash check stops
 * `/staff` from matching `/staff-list`.
 */
const isPrefixActive = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Every route prefix one leaf claims - its own `to` plus its `matchPaths`. */
const prefixesOf = (item: NavLinkItem): string[] => [
  ...(item.to ? [item.to] : []),
  ...(item.matchPaths ?? []),
];

/** Every prefix the whole sidebar claims, leaves and group children alike. */
const allPrefixes = (items: NavItem[]): string[] =>
  items.flatMap((item) =>
    isNavGroup(item) ? item.children.flatMap(prefixesOf) : prefixesOf(item)
  );

/**
 * The single prefix that owns the current path: the longest one that matches it.
 *
 * Longest rather than any, because sections now nest - "Contracts List" is
 * `/contracts` and "New Contract" is `/contracts/new`, so on the second path the
 * first row matches too and a plain prefix test would light both. The longer
 * claim is the more specific screen, and the shorter one still wins wherever
 * nothing deeper is registered, which is what keeps a detail page (`/contracts/17`)
 * lighting its list.
 */
const activePrefixOf = (pathname: string, prefixes: string[]): string | undefined =>
  prefixes
    .filter((prefix) => isPrefixActive(pathname, prefix))
    .reduce<string | undefined>(
      (best, prefix) => (best && best.length >= prefix.length ? best : prefix),
      undefined
    );

/** Whether this leaf is the row that owns the current path. */
const isItemActive = (activePrefix: string | undefined, item: NavLinkItem): boolean =>
  activePrefix !== undefined && prefixesOf(item).includes(activePrefix);

/** A group is a "branch" when one of its children is the active route. */
const isGroupActive = (activePrefix: string | undefined, group: NavGroupItem): boolean =>
  group.children.some((child) => isItemActive(activePrefix, child));

type RowProps = {
  item: NavLinkItem;
  collapsed: boolean;
  depth: number;
  /** The prefix owning the current path; see `activePrefixOf`. */
  activePrefix: string | undefined;
};

const NavLeaf = ({ item, collapsed, depth, activePrefix }: RowProps): ReactNode => {
  const Icon = item.icon;
  const navTitle = useNavTitle();

  const active = isItemActive(activePrefix, item);
  const body = (
    <SidebarRowContent
      active={active}
      collapsed={collapsed}
      icon={Icon ? <Icon size={20} /> : undefined}
      label={navTitle(item)}
    />
  );

  // An external entry is a plain anchor: `NavLink` would try to route it.
  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        className={cn(sidebarRowClass({ collapsed }), depth > 0 && !collapsed && 'pl-11')}
      >
        {body}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to ?? '#'}
      className={cn(sidebarRowClass({ active, collapsed }), depth > 0 && !collapsed && 'pl-11')}
    >
      {body}
    </NavLink>
  );
};

type GroupProps = {
  item: NavGroupItem;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  activePrefix: string | undefined;
};

/**
 * Disclosure is controlled by the sidebar, not held here: the nav is an
 * accordion, and a row cannot know what the other rows are doing.
 */
const NavGroup = ({ item, collapsed, expanded, onToggle, activePrefix }: GroupProps): ReactNode => {
  const branch = isGroupActive(activePrefix, item);
  const Icon = item.icon;
  const navTitle = useNavTitle();

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(sidebarRowClass({ branch, collapsed }), 'w-full')}
      >
        <SidebarRowContent
          branch={branch}
          collapsed={collapsed}
          icon={Icon ? <Icon size={20} /> : undefined}
          label={navTitle(item)}
          chevron={collapsed ? 'none' : 'expand'}
          expanded={expanded}
        />
      </button>
      {expanded && !collapsed && (
        <ul className="mt-1 space-y-1">
          {item.children.map((child) => (
            <li key={child.id}>
              <NavLeaf item={child} collapsed={collapsed} depth={1} activePrefix={activePrefix} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * Drops entries the session has no permission for.
 *
 * A group whose every child was filtered out is dropped too - an expandable row
 * that opens onto nothing is worse than no row.
 */
const useVisibleNavigation = (navigation: NavItem[]): NavItem[] => {
  const { isReady, granted } = usePermission();

  // An unresolved session grants nothing, so rendering the filter against it would flash an
  // empty sidebar and then fill it in. Draw nothing until we actually know.
  if (!isReady) return [];

  return filterNavByPermission(navigation, granted);
};

export const Sidebar = ({
  navigation,
  brand,
}: {
  navigation: NavItem[];
  brand: ReactNode;
}): ReactNode => {
  const { t } = useTranslation();
  const collapsed = useLayoutStore((state) => state.sidebarCollapsed);
  const items = useVisibleNavigation(navigation);
  const { pathname } = useLocation();

  /**
   * The nav is an accordion: at most one group is open, so opening one closes
   * whichever was open. That is why the open group is tracked here instead of
   * per row.
   *
   * It follows the route: every navigation re-seeds it from the group holding
   * the new page, which is `undefined` when that page belongs to no group. So
   * leaving a group closes it - going Contracts > Contracts List > Member List
   * does not leave Contracts hanging open behind an unrelated screen. Only an
   * explicit toggle opens a group whose route you are not on, and it survives
   * exactly until the next navigation.
   */
  const activePrefix = activePrefixOf(pathname, allPrefixes(items));
  const activeGroupId = items.find(
    (item) => isNavGroup(item) && isGroupActive(activePrefix, item)
  )?.id;
  const [openId, setOpenId] = useState<string | undefined>(activeGroupId);
  const [syncedPath, setSyncedPath] = useState(pathname);

  if (pathname !== syncedPath) {
    setSyncedPath(pathname);
    setOpenId(activeGroupId);
  }

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-ds-border-default bg-ds-bg-sidebar transition-[width] duration-200',
        collapsed ? 'w-[76px]' : 'w-[260px]'
      )}
    >
      <div className="flex h-16 shrink-0 items-center px-4">{brand}</div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label={t('shell.mainNavigation')}>
        <ul className="space-y-1">
          {items.map((item) =>
            isNavGroup(item) ? (
              <NavGroup
                key={item.id}
                item={item}
                collapsed={collapsed}
                expanded={openId === item.id}
                onToggle={() => setOpenId((current) => (current === item.id ? undefined : item.id))}
                activePrefix={activePrefix}
              />
            ) : (
              <li key={item.id}>
                <NavLeaf item={item} collapsed={collapsed} depth={0} activePrefix={activePrefix} />
              </li>
            )
          )}
        </ul>
      </nav>
    </aside>
  );
};
